import './bootstrap';
import 'snifferjs/dist/sniffer';
import 'snifferjs/dist/sniffer';

import { plugin as Inertia, App as InertiaApp } from '@inertiajs/inertia-vue';
import { capitalize, htmlToText, normalizeString } from './filters';
import {
  initialize as initTranslations,
  loadFromServer as loadTranslationsFromServer,
} from './services/translations/loader';

import ApiPlugin from './plugins/Api';
import AsyncComputed from 'vue-async-computed';
import DebugPlugin from './plugins/Debug';
import { Plugin as FragmentPlugin } from 'vue-fragment';
import { InertiaProgress } from '@inertiajs/progress';
import PortalVue from 'portal-vue';
import RouteResolver from './plugins/Routes';
import TranslationList from './plugins/TranslationList';
import Vue from 'vue';
import VueCarousel from 'vue-carousel';
import VueClipboard from 'vue-clipboard2';
import VueEvents from 'vue-events';
import VueFileAgent from 'vue-file-agent';
import VueMeta from 'vue-meta';
import VueObserveVisibility from 'vue-observe-visibility';
import VueTagManager from 'vue-tag-manager';
import VueTimers from 'vue-timers';
import Vuex from 'vuex';
import axios from 'axios';
import axiosCancel from 'axios-cancel';
import toastr from 'toastr';
import * as Sentry from '@sentry/vue';
import { setupURLChangeHandler } from '@/utils/router-utils/routerUtils';

InertiaProgress.init();

const sentryDsn = process?.env?.MIX_SENTRY_DSN_PUBLIC ?? null;

axiosCancel(axios);
// This behaviour should be in api.js apiErrorHandler but until $api is not used everywhere we intercept errors here
axios.interceptors.response.use(undefined, function axiosCatch419Error(error) {
  if (error.response?.status === 419) {
    toastr.options.onHidden = function () {
      window.location.href = '/home';
    };
    toastr.error('Oops, looks like you need to login app');
  }
  return Promise.reject(error);
});

VueClipboard.config.autoSetContainer = true;

const app = document.getElementById('app');

/**
 * Shared data from the backend
 */
const sharedData = JSON.parse(app.dataset.page);

/**
 * Filters
 */
Vue.filter('normalizeString', normalizeString);
Vue.filter('capitalize', capitalize);
Vue.filter('htmlToText', htmlToText);

export default (async () => {
  const ImageResolver = (await import('./plugins/ImageResolver')).default;
  const BootstrapVue = await import('bootstrap-vue');
  const LazyComponent = (await import('v-lazy-component/vue2')).default;
  const store = await import('./services/state/Store');

  // CMRAD - Style guide components global import
  const Btn = (await import('./Atoms/Btn')).default;
  const SubmitBtn = (await import('./Atoms/SubmitBtn')).default;
  const CancelBtn = (await import('./Atoms/CancelBtn')).default;
  const RichEditor = (await import('./Atoms/RichEditor')).default;

  Vue.use(BootstrapVue);
  Vue.use(ImageResolver);
  Vue.use(LazyComponent);
  Vue.use(VueEvents);
  Vue.use(VueTimers);
  Vue.use(VueCarousel);
  Vue.use(VueClipboard);
  Vue.use(VueObserveVisibility);
  Vue.use(Inertia);
  Vue.use(PortalVue);
  Vue.use(FragmentPlugin);
  Vue.use(RouteResolver);
  Vue.use(VueMeta);
  Vue.use(TranslationList);
  Vue.use(DebugPlugin);
  Vue.use(VueFileAgent);
  Vue.use(AsyncComputed);
  Vue.use(ApiPlugin);
  Vue.use(Vuex);
  Vue.use(VueTagManager, {
    gtmId: process.env.MIX_GOOGLE_TASK_MANAGER_ID, // GTM id comes from .env
  });

  Vue.component('RichEditor', RichEditor);
  Vue.component('Btn', Btn);
  Vue.component('SubmitBtn', SubmitBtn);
  Vue.component('CancelBtn', CancelBtn);

  const locale = sharedData?.props?.lang?.iso_code ?? 'en';
  const i18n = initTranslations(locale);
  const { app_tenant: appTenant } = sharedData.props;
  await loadTranslationsFromServer(locale, 'cmrad');
  if (appTenant === 'veheri') {
    await loadTranslationsFromServer(locale, 'veheri');
  }

  if (sentryDsn) {
    Sentry.init({
      Vue,
      dsn: sentryDsn,
      integrations: [new Sentry.Replay()],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.3,

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  }

  setupURLChangeHandler();

  const VueApp = new Vue({
    name: 'Main',
    render: (h) =>
      h(InertiaApp, {
        props: {
          initialPage: sharedData,
          resolveComponent: async (name) => {
            const module = await import(`./Views/${name}`);

            return module.default;
          },
        },
      }),
    i18n,
    store: store.default,
  });

  return VueApp;
})();
